import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { fmtET } from '../utils/time';
import api, { asArray } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { Link } from 'react-router-dom';
import { useUnreadNotifications } from '../components/SignalTabsNav';
import { markSignalsSeen } from '../utils/signalAlerts';

/**
 * Options:
 *  - BOOKED_PROFIT → (sellTarget − buy) / buy
 *  - STOPPED_OUT   → (stop − buy) / buy
 *  - OPEN          → live option premium only (not stock, not raw intrinsic-as-stock)
 * Stocks: live underlying vs entry.
 */
// Two dollar figures for the SAME option position (e.g. entry premium vs live
// premium, or entry premium vs a target/stop premium) should be within the
// same rough order of magnitude — real option premiums are typically well
// under $50 and don't differ from each other by 20x+. If they do, one side is
// almost certainly not actually a premium (a stock price or strike price
// entered by mistake — see AdminSignals.jsx's manual-entry form), and diffing
// them as if they were the same scale produces a nonsensical percentage
// rather than an actually-wrong-but-plausible one. Symmetric on purpose: the
// original guard here only caught the "live" side being wrong-scale; manual
// admin entries showed the mismatch could just as easily be on the "entry"
// side instead.
function scaleMismatched(a, b) {
  if (a == null || b == null) return false;
  return (a > b * 20 && a > 50) || (b > a * 20 && b > 50);
}

function calcGain(entry, live, signalType, isOption, status, target, stopLoss) {
  const e = entry != null ? Number(entry) : null;
  if (e == null || e <= 0) return null;
  const st = (status || 'OPEN').toUpperCase().replace(/\s+/g, '_');

  if (isOption) {
    if (st === 'BOOKED_PROFIT' || st === 'CLOSED') {
      const t = target != null ? Number(target) : null;
      if (t == null || scaleMismatched(e, t)) return null;
      return ((t - e) / e) * 100;
    }
    if (st === 'STOPPED_OUT') {
      const sl = stopLoss != null ? Number(stopLoss) : null;
      if (sl == null || scaleMismatched(e, sl)) return null;
      return ((sl - e) / e) * 100;
    }
    // OPEN: only real/theo option premium (reject stock-scale numbers on
    // EITHER side — entry or live — not just live as before).
    const m = live != null ? Number(live) : null;
    if (m == null || m < 0) return null;
    if (scaleMismatched(e, m)) return null;
    return ((m - e) / e) * 100;
  }

  const m = live != null ? Number(live) : null;
  if (m == null || m < 0) return null;
  if (st === 'BOOKED_PROFIT' || st === 'CLOSED') {
    const t = target != null ? Number(target) : null;
    if (t != null) return signalType === 'STOCK_SELL' ? ((e - t) / e) * 100 : ((t - e) / e) * 100;
  }
  if (st === 'STOPPED_OUT' && stopLoss != null) {
    const sl = Number(stopLoss);
    return signalType === 'STOCK_SELL' ? ((e - sl) / e) * 100 : ((sl - e) / e) * 100;
  }
  if (signalType === 'STOCK_SELL') return ((e - m) / e) * 100;
  return ((m - e) / e) * 100;
}

function SymbolMark({ symbol, companyName }) {
  const letter = (companyName || symbol || '?').trim().charAt(0).toUpperCase();
  const hue = ((symbol || 'A').charCodeAt(0) * 37) % 360;
  return (
    <div
      className="w-9 h-9 rounded-lg flex items-center justify-center text-sm font-bold text-white shrink-0"
      style={{ background: `linear-gradient(135deg, hsl(${hue}, 55%, 45%), hsl(${(hue + 40) % 360}, 50%, 30%))` }}
      title={companyName || symbol}
    >
      {letter}
    </div>
  );
}

function optionKey(s) {
  return `${s.symbol}|${s.expiryDate || ''}|${s.strikePrice || ''}|${s.signalType || ''}`;
}

/** Display company name without the internal [AI] / [AI][1W] / [SCALP] / [SWING] /
 *  [MTF] tracking prefix — the optional bracket group after [AI] is the horizon tag
 *  (e.g. [3D], [1W], [2W], [1M]) stamped by the multi-horizon AI generator. */
function displayCompany(name) {
  if (!name) return '';
  return String(name)
    .replace(/^\[AI\](\[\w+\])?\s*/i, '')
    .replace(/^\[SCALP\]\s*/i, '')
    .replace(/^\[SWING\]\s*/i, '')
    .replace(/^\[MTF\]\s*/i, '')
    .replace(/^\[DAILY\]\s*/i, '')
    .replace(/^\[MHF\](\[[\w-]+\])?\s*/i, '')
    .replace(/^\[IOF\](\[\w+\])?\s*/i, '')
    .replace(/^\[POC\]\s*/i, '')
    .trim();
}

/** AI Rapid Profit (WeeklyAiSignal) uses entryPrice/targetPrice for BOTH stock and
 *  option signals, where the rest of this page (built around StockSignal) expects
 *  options to carry buyTarget/sellTarget1 instead. Mapping those across here means
 *  every other read-site (entry/target extraction, live-quote P/L calc, the closed
 *  list) works unchanged rather than needing a kind==='WEEKLY' branch everywhere. */
function normalizeWeekly(s) {
  const isOption = s.signalType?.startsWith('OPTION');
  return {
    ...s,
    kind: 'WEEKLY',
    buyTarget: isOption ? s.entryPrice : undefined,
    sellTarget1: isOption ? s.targetPrice : undefined,
  };
}

/** % gain from entry to a single exit price, honoring STOCK_SELL / PUT direction
 *  the same way calcGain does — used to price out each rung of the sellTarget1/2/3
 *  ladder independently for the closed-card redesign below. */
function exitGainPct(entry, exitPx, signalType) {
  const e = entry != null ? Number(entry) : null;
  const x = exitPx != null ? Number(exitPx) : null;
  if (e == null || e <= 0 || x == null) return null;
  // Only a short stock gains on a falling price — options (CALL and PUT) are bought
  // contracts whose premium must rise, same as calcGain above.
  const isSell = String(signalType || '').includes('STOCK_SELL');
  return isSell ? ((e - x) / e) * 100 : ((x - e) / e) * 100;
}

/** Signal times are shown in US market time (ET) — see utils/time.js. */
function entryDateOf(s) {
  return fmtET(s.startDate || s.publishedAt) || null;
}

// ---------------------------------------------------------------------------
// Signal cards — one clean layout for open and closed, stock and option:
//   header:  [avatar] SYMBOL [TYPE chip]                      right-side status
//            Company name
//   body:    three label-over-value columns (no wrapping on a phone)
//   footer:  one quiet line of dates / state
// ---------------------------------------------------------------------------

/** "$798" / "$295.20" — whole numbers as-is, anything with cents to 2 decimals (was
 *  printed raw, so a 295.2 stop showed as "$295.2"). */
const money = (v) => {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `$${Number.isInteger(n) ? n : n.toFixed(2)}`;
};

/** "+5.00%" / "-1.20%" / "—". */
const pctText = (p) => (p != null && Number.isFinite(p) ? `${p >= 0 ? '+' : ''}${p.toFixed(2)}%` : '—');
const pctTone = (p) => (p == null || !Number.isFinite(p) ? 'text-slate-400' : p >= 0 ? 'text-emerald-400' : 'text-rose-400');

/** "2026-09-28" -> "Sep 28, 2026" (date-only; noon avoids any timezone day-shift).
 *  short: "Sep 28", or "Jan 15 '27" when it's not this year — fits one phone line. */
function fmtExpiry(d, short = false) {
  if (!d) return '';
  const dt = new Date(`${String(d).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(dt.getTime())) return String(d);
  if (!short) return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const md = dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return dt.getFullYear() === new Date().getFullYear() ? md : `${md} '${String(dt.getFullYear()).slice(2)}`;
}

const STATUS_PILL = {
  OPEN: { label: 'OPEN', cls: 'bg-sky-500/15 text-sky-300 ring-sky-400/30' },
  // A Daily Formula pick between clearing criteria and tomorrow's open fill (see
  // SignalAutoCloseService/DailyFormulaSignalService's Fix #1) — entry/target/stop
  // shown are still placeholders until the real open prints.
  PENDING_OPEN: { label: 'PENDING', cls: 'bg-amber-500/15 text-amber-300 ring-amber-400/30' },
  BOOKED_PROFIT: { label: 'BOOKED PROFIT', cls: 'bg-emerald-500/15 text-emerald-300 ring-emerald-400/30' },
  STOPPED_OUT: { label: 'STOPPED OUT', cls: 'bg-rose-500/15 text-rose-300 ring-rose-400/30' },
  CLOSED: { label: 'CLOSED', cls: 'bg-white/5 text-slate-400 ring-white/15' },
};

const CLOSED_RIBBON = {
  BOOKED_PROFIT: { label: 'Booked', bg: 'bg-emerald-500' },
  STOPPED_OUT: { label: 'Stopped', bg: 'bg-rose-500' },
  CLOSED: { label: 'Closed', bg: 'bg-slate-500' },
};

function StatusPill({ st }) {
  const p = STATUS_PILL[st] || STATUS_PILL.CLOSED;
  return (
    <span className={`inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-[9px] font-bold tracking-wide ring-1 ring-inset ${p.cls}`}>
      {p.label}
    </span>
  );
}

/** Avatar + symbol + type chip, company name underneath, `right` on the right. */
function CardHeader({ s, right }) {
  const isBuy = s.signalType?.includes('BUY') || s.signalType?.includes('CALL');
  const company = displayCompany(s.companyName);
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="flex min-w-0 gap-2.5">
        <SymbolMark symbol={s.symbol} companyName={company || s.symbol} />
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <Link to={`/stock/${encodeURIComponent(s.symbol)}`} className="text-lg font-extrabold leading-tight tracking-tight text-white hover:text-emerald-300">
              {s.symbol}
            </Link>
            <span className={`whitespace-nowrap rounded-md px-1.5 py-0.5 text-[9px] font-bold tracking-wide ring-1 ring-inset ${
              isBuy ? 'bg-emerald-500/10 text-emerald-300 ring-emerald-400/40' : 'bg-rose-500/10 text-rose-300 ring-rose-400/40'
            }`}>
              {(s.signalType || '').replace(/_/g, ' ')}
            </span>
          </div>
          {company && <p className="truncate text-xs text-slate-400">{company}</p>}
        </div>
      </div>
      <div className="shrink-0 text-right">{right}</div>
    </div>
  );
}

/** Three evenly spaced label-over-value columns. */
function StatRow({ children }) {
  return <div className="mt-3 grid grid-cols-3 gap-2 text-center">{children}</div>;
}
function Stat({ label, children, sub }) {
  return (
    <div className="min-w-0">
      <p className="text-[11px] text-slate-400">{label}</p>
      <div className="text-base font-bold tabular-nums leading-tight">{children}</div>
      {sub && <div className="text-[10px] leading-tight text-slate-400">{sub}</div>}
    </div>
  );
}

const cardCls = 'rounded-xl border border-slate-700/60 bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 p-3.5 text-slate-100 shadow-md';

/** Closed / booked / stopped signal. The big number is the trade's result: the
 *  admin's override if set, else the stop (stopped out) or Target 1 (booked). */
function ClosedSignalCard({ s }) {
  const isOption = s.signalType?.startsWith('OPTION');
  const entry = isOption ? (s.buyTarget ?? s.entryPrice) : s.entryPrice;
  const exit1 = isOption ? s.sellTarget1 : s.targetPrice;
  const st = (s.status || (s.endDate ? 'CLOSED' : 'OPEN')).toUpperCase().replace(/\s+/g, '_');

  const overrideGain = s.gainOverridePct != null && s.gainOverridePct !== '' ? Number(s.gainOverridePct) : null;
  const result = overrideGain != null
    ? overrideGain
    : (st === 'STOPPED_OUT' && s.stopLoss != null
        ? exitGainPct(entry, s.stopLoss, s.signalType)
        : exitGainPct(entry, exit1, s.signalType));

  const entered = entryDateOf(s);
  const closed = s.endDate ? fmtET(s.endDate) : '';
  const ribbon = CLOSED_RIBBON[st] || CLOSED_RIBBON.CLOSED;

  return (
    <article className={`relative overflow-hidden ${cardCls}`}>
      {/* Diagonal corner ribbon — closed cards only */}
      <div className="pointer-events-none absolute -left-9 top-3 w-32 -rotate-45 text-center">
        <span className={`block py-0.5 text-[10px] font-bold uppercase tracking-wide text-white shadow ${ribbon.bg}`}>
          {ribbon.label}
        </span>
      </div>
      <CardHeader
        s={s}
        right={(
          <>
            <p className={`text-2xl font-extrabold leading-tight tabular-nums ${pctTone(result)}`}>{pctText(result)}</p>
            <StatusPill st={st} />
          </>
        )}
      />
      <StatRow>
        <Stat label={isOption ? 'Buy' : 'Entry'}><span className="text-white">{money(entry)}</span></Stat>
        <Stat label={isOption ? 'Sell' : 'Target'}><span className="text-emerald-400">{money(exit1)}</span></Stat>
        <Stat label="Stop Loss"><span className="text-rose-400">{money(s.stopLoss)}</span></Stat>
      </StatRow>
      {isOption && (s.strikePrice != null || s.expiryDate) && (
        <p className="mt-2.5 truncate whitespace-nowrap text-center text-[11px] text-slate-400">
          {s.strikePrice != null && <>Strike <span className="text-slate-200">${s.strikePrice}</span></>}
          {s.strikePrice != null && s.expiryDate && ' · '}
          {s.expiryDate && <>Exp <span className="text-slate-200">{fmtExpiry(s.expiryDate, true)}</span></>}
        </p>
      )}
      {(entered || closed) && (
        // One line on a phone: smaller text, and "ET" once at the end instead of twice.
        <p className="mt-2.5 truncate whitespace-nowrap border-t border-white/10 pt-2 text-[10px] tracking-tight text-slate-400">
          {entered && <>Entered {closed ? entered.replace(/ ET$/, '') : entered}</>}
          {entered && closed && ' · '}
          {closed && <>Closed {closed}</>}
        </p>
      )}
    </article>
  );
}

/** Open (or pending) signal with live prices. Current Price is the stock — the
 *  underlying for options — and Gain/Loss is the position's P/L (the option premium
 *  for options, see calcGain), with the day move / live premium underneath. */
function OpenSignalCard({ s, q, oq }) {
  const isOption = s.signalType?.startsWith('OPTION');
  const entry = isOption ? (s.buyTarget ?? s.entryPrice) : s.entryPrice;
  const target = isOption ? s.sellTarget1 : s.targetPrice;
  const st = (s.status || 'OPEN').toUpperCase().replace(/\s+/g, '_');

  const stockPx = oq?.stockPrice != null ? Number(oq.stockPrice) : (q?.price != null ? Number(q.price) : null);
  let optionPx = oq?.optionPrice != null ? Number(oq.optionPrice) : (oq?.price != null ? Number(oq.price) : null);
  // Never treat a stock-scale number as an option premium for display/P/L
  if (optionPx != null && entry != null && optionPx > Number(entry) * 20 && optionPx > 50) optionPx = null;
  if ((oq?.priceSource || '') === 'INTRINSIC' && optionPx != null && optionPx > 50) optionPx = null;

  const livePx = isOption ? optionPx : (q?.price != null ? Number(q.price) : null);
  const autoGain = calcGain(entry, livePx, s.signalType, isOption, st, target, s.stopLoss);
  const gainPct = s.gainOverridePct != null && s.gainOverridePct !== '' ? Number(s.gainOverridePct) : autoGain;

  const dayChg = q?.changePercent != null ? Number(q.changePercent) : null;
  const priceUp = isOption
    ? (gainPct != null ? gainPct >= 0 : (dayChg != null ? dayChg >= 0 : null))
    : (dayChg != null ? dayChg >= 0 : (gainPct != null ? gainPct >= 0 : null));
  const priceTone = priceUp == null ? 'text-white' : priceUp ? 'text-emerald-400' : 'text-rose-400';

  const pending = st === 'PENDING_OPEN';
  const pushed = publishedDateTime(s) || (s.startDate ? fmtET(s.startDate) : '');

  return (
    <article className={cardCls}>
      <CardHeader s={s} right={<StatusPill st={pending ? 'PENDING_OPEN' : 'OPEN'} />} />
      <StatRow>
        <Stat label="Current Price">
          <span className={priceTone}>{stockPx != null ? `$${stockPx.toFixed(2)}` : '…'}</span>
        </Stat>
        <Stat
          label="Gain/Loss"
          sub={isOption
            ? (optionPx != null ? `Prem $${optionPx.toFixed(2)}${oq?.liveQuote === false ? ' est.' : ''}` : null)
            : (dayChg != null && Number.isFinite(dayChg)
                ? <span className={pctTone(dayChg)}>Day {pctText(dayChg)}</span>
                : null)}
        >
          <span className={pctTone(gainPct)}>{pctText(gainPct)}</span>
        </Stat>
        <Stat label="Exit Targets">
          {/* Arrow column + value column, so ▲ and ▼ rows line up whatever the
              value widths (centering each line separately left them staggered). */}
          <span className="inline-grid grid-cols-[auto_auto] items-center gap-x-1 text-left">
            <span className="text-[10px] text-emerald-400">▲</span>
            <span className="text-emerald-400">{money(target)}</span>
            <span className="text-[10px] text-rose-400">▼</span>
            <span className="text-rose-400">{money(s.stopLoss)}</span>
          </span>
        </Stat>
      </StatRow>
      {/* One line on a phone: no colons, short expiry ("Sep 28" / "Jan 15 '27"). */}
      <p className="mt-2.5 truncate whitespace-nowrap text-center text-[11px] text-slate-400">
        {isOption ? (
          <>
            Buy <span className="text-slate-200">{money(entry)}</span>
            {' · '}Sell <span className="text-slate-200">{money(target)}</span>
            {s.strikePrice != null && <>{' · '}Strike <span className="text-slate-200">${s.strikePrice}</span></>}
            {s.expiryDate && <>{' · '}Exp <span className="text-slate-200">{fmtExpiry(s.expiryDate, true)}</span></>}
          </>
        ) : (
          <>Entry <span className="text-slate-200">{money(entry)}</span></>
        )}
      </p>
      <div className="mt-2.5 flex items-center justify-between border-t border-white/10 pt-2 text-[11px] text-slate-400">
        <span>{pushed ? `Pushed ${pushed}` : ''}</span>
        <span className={`inline-flex items-center gap-1 font-semibold ${pending ? 'text-amber-300' : 'text-emerald-400'}`}>
          <span className={`h-1.5 w-1.5 rounded-full ${pending ? 'bg-amber-400' : 'animate-pulse bg-emerald-400'}`} />
          {pending ? 'Pending open' : 'Active'}
        </span>
      </div>
    </article>
  );
}

/** Small square icon button with a tiny label under it, for the Signals header.
 *  `to` makes it a link; otherwise it's a button (`onClick`). `children` are the
 *  SVG path(s) of a 24x24 stroke icon. */
function HeaderIcon({ to, onClick, label, active = false, dot = false, children }) {
  const cls = `relative flex w-11 flex-col items-center gap-0.5 rounded-xl py-1 transition ${
    active ? 'bg-indigo-50 text-indigo-600' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
  }`;
  const inner = (
    <>
      <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
        {children}
      </svg>
      <span className="text-[9px] font-semibold leading-none">{label}</span>
      {dot && <span className="absolute right-2 top-1 h-2 w-2 rounded-full bg-rose-500 ring-2 ring-slate-50" />}
    </>
  );
  return to
    ? <Link to={to} className={cls} aria-label={label}>{inner}</Link>
    : <button type="button" onClick={onClick} className={cls} aria-label={label}>{inner}</button>;
}

function publishedDateTime(s) {
  return s.publishedAt ? fmtET(s.publishedAt) || null : null;
}

function isClosedSignal(s) {
  const st = (s.status || '').toUpperCase().replace(/\s+/g, '_');
  if (s.endDate) return true;
  return ['BOOKED_PROFIT', 'STOPPED_OUT', 'CLOSED'].includes(st);
}

/** Last-seen signals + quotes, per user, so a revisit (or app relaunch) paints the
 *  page instantly from the previous visit while fresh data loads behind it. Browser
 *  storage can be unavailable/full — every access is best-effort. */
const CACHE_PREFIX = 'sx-signals-cache-v1:';
function cacheKey(user) {
  return CACHE_PREFIX + (user?.id ?? user?.email ?? 'anon');
}
function readSignalsCache(user) {
  try {
    const raw = localStorage.getItem(cacheKey(user));
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}
function writeSignalsCache(user, data) {
  try {
    localStorage.setItem(cacheKey(user), JSON.stringify(data));
  } catch (_) {}
}

export default function Signals() {
  const { user } = useAuth();
  const [cached] = useState(() => (user?.subscriptionActive ? readSignalsCache(user) : null));
  const [signals, setSignals] = useState(() => cached?.signals || []);
  const [quotes, setQuotes] = useState(() => cached?.quotes || {});
  const [optionQuotes, setOptionQuotes] = useState(() => cached?.optionQuotes || {});
  const [loading, setLoading] = useState(() => !cached);
  const [lastRefresh, setLastRefresh] = useState(() => (cached?.savedAt ? new Date(cached.savedAt) : null));
  // Live quote page OFF for this user (never for admin/semi-admin — SettingsContext):
  // prices come from /signals/quotes on the admin's interval, labelled "as of".
  const { liveQuotePageEnabled, signalPriceRefreshMinutes } = useSettings();
  const livePrices = liveQuotePageEnabled !== false;
  const refreshMinutes = signalPriceRefreshMinutes || 10;
  const [pricesAsOf, setPricesAsOf] = useState(null);
  const [filter, setFilter] = useState('OPEN');          // status tab: ALL | OPEN | CLOSED
  const [typeFilter, setTypeFilter] = useState('ALL');   // funnel: ALL | STOCK | OPTION
  const [typeMenuOpen, setTypeMenuOpen] = useState(false);
  const unreadNotifications = useUnreadNotifications();
  const [clock, setClock] = useState(() => new Date());

  // Visiting this page is itself "seen" — clears the header bell badge (see Sidebar)
  // for newly published signals, same as visiting AI Rapid Profit does. Uses the
  // shared helper (not a local localStorage write) so it also fires the
  // 'sx-signals-seen' event Sidebar listens for — clears the badge immediately
  // instead of waiting for its next 30s poll.
  useEffect(() => {
    if (!user) return;
    markSignalsSeen(user);
  }, [user]);

  const loadQuotes = useCallback(async (list) => {
    const stockMap = {};
    const optMap = {};
    // Only OPEN signals render live prices (closed ones use ClosedSignalCard, which
    // never reads quotes), and option cards take the underlying's price from the
    // option context itself — so stock quotes are only needed for open stock signals.
    const open = (list || []).filter(s => !isClosedSignal(s));
    const stockSymbols = [...new Set(
      open.filter(s => !s.signalType?.startsWith('OPTION')).map(s => s.symbol).filter(Boolean)
    )];
    const optSignals = open.filter(s => s.signalType?.startsWith('OPTION'));

    // Live quote page ON (and always for admin/semi-admin): live per-symbol prices.
    // OFF: ONE call to /signals/quotes, which only covers open signals' symbols and
    // refreshes each from the data provider at most once per the admin's interval.
    let asOf = null;
    const stockFetches = livePrices
      ? stockSymbols.map(async (sym) => {
          try {
            const res = await api.get(`/stock/price/${sym}`);
            if (res.data?.success) stockMap[sym] = res.data;
          } catch (_) {}
        })
      : [(async () => {
          try {
            const res = await api.get('/signals/quotes');
            const all = res.data?.quotes || {};
            for (const sym of stockSymbols) {
              const q = all[sym];
              if (!q) continue;
              stockMap[sym] = q;
              if (q.fetchedAt && (asOf == null || q.fetchedAt < asOf)) asOf = q.fetchedAt;
            }
          } catch (_) {}
        })()];

    // All in parallel; the backend shares results across users, so this stays cheap.
    await Promise.all([
      ...stockFetches,
      ...optSignals.map(async (s) => {
        try {
          const right = s.signalType?.includes('PUT') ? 'PUT' : 'CALL';
          const params = new URLSearchParams({ symbol: s.symbol, right });
          if (s.expiryDate) params.set('expiry', s.expiryDate);
          if (s.strikePrice != null) params.set('strike', String(s.strikePrice));
          const res = await api.get(`/stock/option?${params}`);
          if (res.data) optMap[optionKey(s)] = res.data;
        } catch (_) {}
      }),
    ]);

    setQuotes(stockMap);
    setOptionQuotes(optMap);
    setLastRefresh(new Date());
    setPricesAsOf(livePrices ? null : (asOf ? new Date(asOf) : null));
  }, [livePrices]);

  useEffect(() => {
    if (!user?.subscriptionActive) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        // Merged with AI Rapid Profit (was its own separate page) — one unified
        // Signals page/list now.
        const [plainRes, weeklyRes] = await Promise.all([
          api.get('/signals/published'),
          api.get('/weekly-signals/published').catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        const list = [
          ...asArray(plainRes.data),
          ...asArray(weeklyRes.data).map(normalizeWeekly),
        ];
        setSignals(list);
        // Show the signal cards now — prices fill in as quotes arrive, instead of the
        // whole page sitting on "Loading…" until every quote call has finished.
        setLoading(false);
        loadQuotes(list);
      } catch (_) {
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user, loadQuotes]);

  useEffect(() => {
    if (!user?.subscriptionActive || signals.length === 0) return;
    writeSignalsCache(user, {
      signals,
      quotes,
      optionQuotes,
      savedAt: (lastRefresh || new Date()).toISOString(),
    });
  }, [user, signals, quotes, optionQuotes, lastRefresh]);

  useEffect(() => {
    if (!user?.subscriptionActive || signals.length === 0) return;
    // Live: every 30s. Live quote page OFF: the admin's interval (5/10/15 min).
    const every = livePrices ? 30000 : refreshMinutes * 60000;
    const id = setInterval(() => loadQuotes(signals), every);
    return () => clearInterval(id);
  }, [user, signals, loadQuotes, livePrices, refreshMinutes]);

  useEffect(() => {
    const id = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const closedAt = (s) => {
    if (s.endDate) return new Date(s.endDate).getTime();
    return s.publishedAt ? new Date(s.publishedAt).getTime() : 0;
  };

  const DAY = 24 * 60 * 60 * 1000;
  const now = Date.now();

  // Type filter (funnel icon) — applies under every status tab (All / Open / Closed).
  const isOptionSig = (s) => !!s.signalType?.startsWith('OPTION');
  const typeOk = (s) => typeFilter === 'ALL' || (typeFilter === 'OPTION') === isOptionSig(s);

  // Latest pushed signal first, by default — no user-facing sort control, just the
  // right default (was unordered for the active list, and ordered by closedAt for
  // the closed one — both now consistently newest-published-first).
  const publishedTime = (s) => s.publishedAt ? new Date(s.publishedAt).getTime() : 0;
  const sortByPublished = useCallback((list) => {
    return [...list].sort((a, b) => publishedTime(b) - publishedTime(a));
  }, []);

  const { activeList, closedList } = useMemo(() => {
    const active = [];
    const closed = [];
    for (const s of signals) {
      if (!typeOk(s)) continue;
      if (!isClosedSignal(s)) {
        active.push(s);
        continue;
      }
      const t0 = closedAt(s);
      const age = now - t0;
      // After the next calendar day of close → move to Closed/Expired section
      if (t0 && age < 1 * DAY) {
        active.push(s);
      } else if (t0 && age <= 90 * DAY) {
        closed.push(s);
      }
    }
    const closedSorted = sortByPublished(closed);

    if (filter === 'CLOSED') {
      // Show only closed/expiry/stopped in main area
      const allClosed = sortByPublished([...active.filter(isClosedSignal), ...closed]);
      return { activeList: allClosed, closedList: [] };
    }
    if (filter === 'ALL') {
      return { activeList: sortByPublished(active), closedList: closedSorted };
    }
    // OPEN → open only in main list; closed section only for ALL
    return { activeList: sortByPublished(active.filter((s) => !isClosedSignal(s))), closedList: [] };
  }, [signals, filter, typeFilter, sortByPublished]);

  const filtered = activeList;

  if (!user?.subscriptionActive) {
    return (
      <div className="text-center py-20">
        <h2 className="text-xl font-bold mb-2 text-slate-900">Subscription required</h2>
        <p className="text-slate-500 mb-4">Activate your plan to view signals.</p>
        <Link to="/subscribe" className="inline-block bg-slate-900 text-white px-6 py-2.5 rounded-xl">Subscribe</Link>
      </div>
    );
  }

  if (loading) {
    return <div className="py-20 text-center text-slate-400">Loading signals…</div>;
  }

  return (
    <div className="pb-8">
      {/* Header: title + live time/session on the left; icon buttons on the right —
          Today's AI, type filter (stocks / options), Notifications. Replaces the old
          Signals / Today's AI Signal / Notifications tab strip and the 5-button filter. */}
      <div className="mb-4 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            {/* Same brand blue→violet as the Signals link on every other page (ui.jsx
                PageHeader) — gradient icon tile + gradient word, styled as a title. */}
            <h1 className="flex min-w-0 items-center gap-2">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-sky-500 to-indigo-600 text-white shadow-md shadow-indigo-500/25 ring-1 ring-inset ring-white/20">
                <svg className="h-[18px] w-[18px]" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <rect x="2" y="11" width="3" height="6" rx="1" opacity="0.6" />
                  <rect x="7" y="8" width="3" height="9" rx="1" opacity="0.8" />
                  <rect x="12" y="5" width="3" height="12" rx="1" />
                  <circle cx="16.5" cy="3.5" r="2" />
                </svg>
              </span>
              <span className="bg-gradient-to-r from-sky-500 to-indigo-600 bg-clip-text text-[26px] font-extrabold leading-none tracking-tight text-transparent">
                Signals
              </span>
            </h1>
            {(() => {
              // Color by US equity session (approx ET): pre=amber, open=green, after=sky, closed=slate
              const et = new Date(clock.toLocaleString('en-US', { timeZone: 'America/New_York' }));
              const day = et.getDay(); // 0 Sun
              const mins = et.getHours() * 60 + et.getMinutes();
              const weekend = day === 0 || day === 6;
              let session = 'Closed';
              let color = 'text-slate-400';
              let dot = 'bg-slate-400';
              if (!weekend) {
                if (mins >= 4 * 60 && mins < 9 * 60 + 30) {
                  session = 'Pre-market'; color = 'text-amber-600'; dot = 'bg-amber-500';
                } else if (mins >= 9 * 60 + 30 && mins < 16 * 60) {
                  session = 'Market open'; color = 'text-emerald-600'; dot = 'bg-emerald-500 animate-pulse';
                } else if (mins >= 16 * 60 && mins < 20 * 60) {
                  session = 'After hours'; color = 'text-sky-600'; dot = 'bg-sky-500';
                }
              }
              // Viewer's own local date/time (the session dot is computed in ET above).
              const dateStr = clock.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
              const timeStr = clock.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
              // One line, never wraps: small text, short date ("Sep 26").
              return (
                <p className="mt-1 flex items-center gap-1 whitespace-nowrap text-[10px] leading-none">
                  <span className="font-semibold tabular-nums text-slate-700">{timeStr}</span>
                  <span className="text-slate-400">· {dateStr} ·</span>
                  <span className={`inline-flex items-center gap-0.5 font-semibold ${color}`}>
                    <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
                    {session}
                  </span>
                </p>
              );
            })()}
          </div>

          <div className="flex shrink-0 items-start gap-1">
            <HeaderIcon to="/ai-signals" label="AI">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.8 3.5l1.3 3.9a2 2 0 001.3 1.3l3.9 1.3-3.9 1.3a2 2 0 00-1.3 1.3L9.8 16.5l-1.3-3.9a2 2 0 00-1.3-1.3L3.3 10l3.9-1.3a2 2 0 001.3-1.3L9.8 3.5z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M18 3v4M16 5h4M17.5 15.5v3M16 17h3" />
            </HeaderIcon>

            <div className="relative">
              <HeaderIcon
                label={typeFilter === 'STOCK' ? 'Stocks' : typeFilter === 'OPTION' ? 'Options' : 'Filter'}
                active={typeFilter !== 'ALL'}
                onClick={() => setTypeMenuOpen((o) => !o)}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 5h18l-7 8v5l-4 2v-7L3 5z" />
              </HeaderIcon>
              {typeMenuOpen && (
                <>
                  {/* Invisible backdrop: tap anywhere outside to close. */}
                  <button type="button" aria-label="Close filter" className="fixed inset-0 z-40 cursor-default" onClick={() => setTypeMenuOpen(false)} />
                  <div className="absolute right-0 z-50 mt-1 w-40 overflow-hidden rounded-xl bg-white py-1 shadow-lg ring-1 ring-slate-200">
                    {[['ALL', 'All types'], ['STOCK', 'Stocks'], ['OPTION', 'Options']].map(([k, label]) => (
                      <button
                        key={k}
                        type="button"
                        onClick={() => { setTypeFilter(k); setTypeMenuOpen(false); }}
                        className={`flex w-full items-center justify-between px-3.5 py-2 text-left text-sm transition hover:bg-slate-50 ${
                          typeFilter === k ? 'font-semibold text-indigo-600' : 'text-slate-700'
                        }`}
                      >
                        {label}
                        {typeFilter === k && <span aria-hidden="true">✓</span>}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            <HeaderIcon to="/notifications" label="Alerts" dot={unreadNotifications}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.4-1.4A2 2 0 0118 14.2V11a6 6 0 00-4-5.7V5a2 2 0 10-4 0v.3C7.7 6.2 6 8.4 6 11v3.2c0 .5-.2 1-.6 1.4L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </HeaderIcon>
          </div>
        </div>

        {/* Status tabs — full width, three equal parts. */}
        <div className="grid grid-cols-3 gap-1 rounded-xl bg-slate-100 p-1">
          {[
            { id: 'ALL', label: 'All' },
            { id: 'OPEN', label: 'Open' },
            { id: 'CLOSED', label: 'Closed' },
          ].map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setFilter(f.id)}
              className={`rounded-lg py-1.5 text-sm transition ${
                filter === f.id ? 'bg-slate-900 font-semibold text-white shadow-sm' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {!livePrices && (
        <p className="-mt-2 mb-3 text-[11px] text-slate-400">
          {pricesAsOf
            ? `Prices as of ${pricesAsOf.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })} · updates every ${refreshMinutes} min`
            : `Prices update every ${refreshMinutes} min`}
        </p>
      )}

      {filtered.length === 0 && closedList.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-10 text-center text-slate-400 text-sm">
          No signals in this filter.
        </div>
      ) : (
        <>
        {filtered.length > 0 && (
        <div className="grid gap-3">
          {filtered.map((s) => {
            // Closed/booked/stopped/expired signals get the result card (same one used
            // in the "Closed / Expired" section below) instead of the live open card —
            // this is what the "Closed" filter tab actually renders.
            if (isClosedSignal(s)) {
              return <ClosedSignalCard key={`f-${s.kind || 'STOCK'}-${s.id}`} s={s} />;
            }
            return <OpenSignalCard key={`${s.kind || 'STOCK'}-${s.id}`} s={s} q={quotes[s.symbol]} oq={optionQuotes[optionKey(s)]} />;
          })}
        </div>
        )}

        {filter === 'ALL' && closedList.length > 0 && (
          <div className="mt-10">
            <div className="mb-3">
              <h2 className="text-lg font-bold text-slate-800">Closed / Expired</h2>
              <p className="text-xs text-slate-400">
                Booked profit, stopped out, closed & expired options — day after close, kept up to 3 months
              </p>
            </div>
            <div className="grid gap-3">
              {closedList.map((s) => (
                <ClosedSignalCard key={`c-${s.kind || 'STOCK'}-${s.id}`} s={s} />
              ))}
            </div>
          </div>
        )}
        </>
      )}
    </div>
  );
}
